import { AxiosError, AxiosResponse } from "axios";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import { queryKeys } from "../../constants/queryKeys";
import {
  ReferralDashboardResponse,
  ReferralCodeResponse,
  ReferralHistoryResponse,
  UpdateReferralCodeRequest,
  ReferralReward,
  ReferralRewardData,
  ReferralStats
} from "../../types/referral.type";
import { referralEndpoint } from "../endpoints";
import http from "../http.service";
import { createURLSearchParams } from "../../utils/common";

// Utility functions to transform API data
export const transformRewardData = (rewardData: ReferralRewardData): ReferralReward => {
  return {
    id: rewardData.id,
    type: rewardData.type,
    description: rewardData.description,
    value: rewardData.value,
    expiresAt: rewardData.expires_at ? new Date(rewardData.expires_at) : undefined,
    isActive: rewardData.is_active,
  };
};

export const transformStatsData = (statsData: any): ReferralStats => {
  return {
    totalReferrals: statsData.total_referrals || 0,
    successfulReferrals: statsData.successful_referrals || 0,
    totalRewards: statsData.total_rewards || 0,
    activeRewardsCount: statsData.active_rewards_count || 0,
  };
};

// Get referral dashboard data (stats + active rewards)
export const useGetReferralDashboard = () => {
  const query = useQuery(
    [queryKeys.GET_REFERRAL_DASHBOARD],
    async () => {
      const res = await http.get<ReferralDashboardResponse>(referralEndpoint.DASHBOARD);
      return res;
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  const rawData = query.data?.data?.data;
  const transformedStats = rawData?.statistics ? transformStatsData(rawData.statistics) : undefined;
  const transformedRewards = rawData?.active_rewards?.map(transformRewardData) || [];

  return {
    ...query,
    dashboard: rawData,
    stats: transformedStats,
    activeRewards: transformedRewards,
  };
};

// Get referral code and link
export const useGetReferralCode = () => {
  const query = useQuery(
    [queryKeys.GET_REFERRAL_CODE],
    async () => {
      const res = await http.get<ReferralCodeResponse>(referralEndpoint.GET_CODE);
      return res;
    },
    {
      staleTime: Infinity, // Cache indefinitely until manually invalidated
      cacheTime: Infinity,
    }
  );

  return {
    ...query,
    codeData: query.data?.data?.data,
    referralCode: query.data?.data?.data?.referral_code,
    referralLink: query.data?.data?.data?.referral_link,
  };
};

// Update referral code
export const useUpdateReferralCode = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    AxiosResponse<ReferralCodeResponse>,
    AxiosError<any>,
    UpdateReferralCodeRequest,
    unknown
  >({
    mutationFn: async (data: UpdateReferralCodeRequest) => {
      return await http.put<ReferralCodeResponse>(referralEndpoint.UPDATE_CODE, data);
    },
    onSuccess: () => {
      // Invalidate and refetch referral code
      queryClient.invalidateQueries([queryKeys.GET_REFERRAL_CODE]);
      // Also invalidate dashboard to update any related stats
      queryClient.invalidateQueries([queryKeys.GET_REFERRAL_DASHBOARD]);
    },
  });

  return mutation;
};

// Get referral history with pagination
export const useGetReferralHistory = (page: number = 1, perPage: number = 10) => {
  const query = useQuery(
    [queryKeys.GET_REFERRAL_HISTORY, page, perPage],
    async () => {
      const params = createURLSearchParams({
        page,
        per_page: perPage,
      });
      const url = `${referralEndpoint.HISTORY}?${params}`;
      const res = await http.get<ReferralHistoryResponse>(url);
      return res;
    },
    {
      staleTime: 1 * 60 * 1000, // 1 minute
      cacheTime: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true, // Keep previous data while fetching new page
    }
  );

  return {
    ...query,
    referrals: query.data?.data?.data || [],
    meta: query.data?.data?.meta,
  };
};

// Get referral history with infinite scroll
export const useGetReferralHistoryInfinite = (perPage: number = 10) => {
  const query = useInfiniteQuery(
    [queryKeys.GET_REFERRAL_HISTORY, "infinite", perPage],
    async ({ pageParam = 1 }) => {
      const params = createURLSearchParams({
        page: pageParam,
        per_page: perPage,
      });
      const url = `${referralEndpoint.HISTORY}?${params}`;
      const res = await http.get<ReferralHistoryResponse>(url);
      return res;
    },
    {
      getNextPageParam: (lastPage) => {
        const meta = lastPage?.data?.meta;
        if (!meta) return undefined;

        const { current_page, last_page } = meta;
        return current_page < last_page ? current_page + 1 : undefined;
      },
      staleTime: 1 * 60 * 1000, // 1 minute
      cacheTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  return {
    ...query,
    referrals: query.data?.pages?.flatMap(page => page.data?.data || []) || [],
  };
};

// Get recent referral history (for dashboard)
export const useGetRecentReferralHistory = (limit: number = 5) => {
  const query = useQuery(
    [queryKeys.GET_REFERRAL_HISTORY, "recent", limit],
    async () => {
      const params = createURLSearchParams({
        page: 1,
        per_page: limit,
      });
      const url = `${referralEndpoint.HISTORY}?${params}`;
      const res = await http.get<ReferralHistoryResponse>(url);
      return res;
    },
    {
      staleTime: 1 * 60 * 1000, // 1 minute
      cacheTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  return {
    ...query,
    recentReferrals: query.data?.data?.data || [],
  };
};
